import { Icon } from "@iconify/react";
import {
  Button,
  Col,
  Form,
  Input,
  InputNumber,
  Row,
  Space,
  Steps,
  Typography,
  Upload,
  UploadProps,
  Checkbox,
  Image,
  message,
  Tooltip,
  Modal,
} from "antd";
import React, { useState, ReactNode, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "../../../../Hooks/useQuery";
import * as yup from "yup";
import { ContentWrapper } from "../../../../Components/Layout/ContentWrapper";
import { ErrorMessage, Field, FieldProps, Formik } from "formik";
import { useAuth } from "../../../../Hooks/useAuth";
import { useNotification } from "~/Hooks/useNotification";
import { get, post, put } from "@services/api.service";
import { SuccessModal } from "@components/SuccessModal/SuccessModal";
import CustomOptions from "../../SubModules/CustomOptions/CustomOptions";
import { allowedDomainsAll, UserType } from "~/Utils";
import styles from "../../Contract.module.scss";
import { panelApiUrl } from "~/Utils";

interface MailFormItem {
  emailSubject: string;
  emailMessage: string;
}

interface ReminderFormItem {
  sendAutomaticRemainders: boolean;
  daysUntilFirstRemainder: number;
  daysBetweenRemainder: number;
}

interface ExpirationFormItem {
  expiryEnabled: boolean;
  expiryAfter: number;
  expiryWarnBefore: number;
}

interface FormItems {
  setSigningOrder: boolean;
  defaultMail: MailFormItem;
  reviewMail: MailFormItem;
  approveMail: MailFormItem;
  completeMail: MailFormItem;
  declineMail: MailFormItem;
  reminderMail: MailFormItem;
  copyMail: MailFormItem;
  oneStopMail: MailFormItem;
  expiryMail: MailFormItem;
  higherAuthorityMail: MailFormItem;
  creatorMail: MailFormItem;
  reSubmitMail: MailFormItem;
  cancelMail: MailFormItem;
  contracts?: any;
  templateId?: string;
  templateName?: string;
  templateDescription?: string;
  reminderEnabled?: boolean;
  reminderFrequency?: string;
  reminderDelay?: string;
  expiryAfter?: string;
  expiryWarn?: string;
  reminder: ReminderFormItem;
  expiration: ExpirationFormItem;
  // signerCanSignOnMobile: boolean;
  recipientLock?: boolean;
  messageLock?: boolean;
  allowComments?: boolean;
}

const defaultValue = {
  templateId: "",
  templateName: "",
  templateDescription: "",
  defaultMail: {
    emailSubject: "",
    emailMessage: "",
  },
  reviewMail: {
    emailSubject: "Work Order : Request {refNo} for your review ({designation})",
    emailMessage: "Greetings! You have been requested to review the document.",
  },
  approveMail: {
    emailSubject: "Work Order : Request {refNo} has been Approved ({designation})",
    emailMessage: "Greetings! The request is now approved.",
  },
  completeMail: {
    emailSubject: "Work Order : Request {refNo} is Completed",
    emailMessage: "Greetings! The request is now completed.",
  },
  declineMail: {
    emailSubject: "Work Order : Request {refNo} Declined ({designation})",
    emailMessage: "The request has been declined.",
  },
  reminderMail: {
    emailSubject: "Work Order : Reminder on Request {refNo} for your review ({designation})",
    emailMessage: "Greetings! You are reminded to review the document",
  },
  copyMail: {
    emailSubject: "Work Order: Request {refNo}’s review feedback",
    emailMessage: "Greetings! You have been requested to review the comments on Project {refNo} - ‘Project Name’. Please review the comments and take the necessary action.\n\nComments:",
  },
  expiryMail: {
    emailSubject: "Work Order : Request {refNo} has expired",
    emailMessage: "Greetings! The request {refNo} has expired.",
  },
  higherAuthorityMail: {
    emailSubject: "Work Order : Request {refNo} for your review ({designation})",
    emailMessage: "Hi, ({designation})\nThank you for your feedback on the previous version of the \"Project Name\". I have carefully considered your points and made revisions to address them in the attached version ({Version No.}) of the Work Order.\nAwaiting for your review and approval.",
  },
  creatorMail: {
    emailSubject: "Work Order: Request {refNo} is for your review (SPA Creator)",
    emailMessage: "Hi, SPA CREATOR\nI have revised the previous version of \"{refNo}\" based on the approver's feedback. Please review the attached version ({Version No.}) at your convenience.",
  },
  reSubmitMail: {
    emailSubject: "Work Order : Request {refNo} is resubmitted for your review ({designation})",
    emailMessage: "Greetings! The request is resubmitted.",
  },
  cancelMail: {
    emailSubject: "Work Order: Request {refNo} has been cancelled",
    emailMessage: "Greetings! The request has been cancelled",
  },
  oneStopMail: {
    emailSubject: "Work Order : Request {refNo} has voided",
    emailMessage: "Greetings! The request {refNo} has been voided.",
  },
  reminder: {
    sendAutomaticRemainders: false,
    daysUntilFirstRemainder: 0,
    daysBetweenRemainder: 0,
  },
  expiration: {
    expiryEnabled: true,
    expiryAfter: 14,
    expiryWarnBefore: 2,
  },
  reminderEnabled: false,
  reminderFrequency: "0",
  reminderDelay: "0",
  expiryAfter: "14",
  expiryWarn: "2",
  // signerCanSignOnMobile: true,
  recipientLock: false,
  messageLock: false,
  allowComments: true,
} as unknown as FormItems;

interface SignerItemProps {
  index: number;
  moveSigner: (from: number, to: number) => void;
  values: FormItems;
  setValues: (values: FormItems) => void;
}

const getItemText = (name: string, value: any, isBoolean?: any) => {
  return (
    <Space direction="vertical">
      <Typography.Text type="secondary">{name}</Typography.Text>
      <Typography.Text>{!isBoolean ? value : value ? "Enabled" : "Disabled"}</Typography.Text>
    </Space>
  );
};
const EqualCol = ({ children }: { children: ReactNode }) => (
  <Col
    xs={24}
    md={12}
  >
    {children}
  </Col>
);

var newDocId = 100;

export const AddNewTemplateDocument = () => {
  const [docsList, setDocsList] = useState<any>([]); // contains all files
  const { user } = useAuth();
  const { openToast, openNotification } = useNotification();
  const [currentStep, setCurrentStep] = useState(0);
  const [name, setName] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const [initialValue, setInitialValue] = useState<FormItems>(defaultValue);
  const [record, setRecord] = useState<any>({});
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const [showSuccess, setShowSuccess] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<any>("");
  const query = useQuery();
  const id = query.get("id");
  const [loadingDocId, setLoadingDocId] = useState<string>("");
  const [deletedIds, setDeletedIds] = useState<any>([]);
  const [selectDocument, setSelectDocument] = useState<any>();
  const [selectedDocumentReview, setSelectedDocumentReview] = useState<string>("");
  const [save, setSave] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);
  const [scale, setScale] = useState<number>(1);

  const zoomIn = () => {
    setScale(scale + 0.1);
  };

  const zoomOut = () => {
    if (scale > 0.1) {
      setScale(scale - 0.1);
    }
  };

  useEffect(() => {
    if (id) {
      setIsLoading(true);
      get(`${panelApiUrl}/template/${id}`)
        .then((res: any) => {
          let dataObj = res?.response?.data;
          if (dataObj && Array.isArray(dataObj.records)) {
            res = dataObj.records[0] || {};
          } else {
            res = dataObj || {};
          }
          setRecord(res);
          const values = {
            templateId: res.templateId ?? id ?? "",
            templateName: res.templateName ?? "",
            templateDescription: res.templateDescription ?? "",
            defaultMail: {
              emailSubject: res?.defaultMail?.emailSubject ?? "",
              emailMessage: res?.defaultMail?.emailMessage ?? "",
            },
            reviewMail: {
              emailSubject: res.reviewMail?.emailSubject ?? "",
              emailMessage: res.reviewMail?.emailMessage ?? "",
            },
            approveMail: {
              emailSubject: res.approveMail?.emailSubject ?? "",
              emailMessage: res.approveMail?.emailMessage ?? "",
            },
            completeMail: {
              emailSubject: res.completeMail?.emailSubject ?? res.completeMail?.emailSubject ?? "",
              emailMessage: res.completeMail?.emailMessage ?? res.completeMail?.emailMessage ?? "",
            },
            declineMail: {
              emailSubject: res.declineMail?.emailSubject ?? "",
              emailMessage: res.declineMail?.emailMessage ?? "",
            },
            reminderMail: {
              emailSubject: res.reminderMail?.emailSubject ?? "",
              emailMessage: res.reminderMail?.emailMessage ?? "",
            },
            copyMail: {
              emailSubject: res.copyMail?.emailSubject ?? "",
              emailMessage: res.copyMail?.emailMessage ?? "",
            },
            expiryMail: {
              emailSubject: res?.expiryMail?.emailSubject ?? "",
              emailMessage: res?.expiryMail?.emailMessage ?? "",
            },
            higherAuthorityMail: {
              emailSubject: res?.higherAuthorityMail?.emailSubject ?? "",
              emailMessage: res?.higherAuthorityMail?.emailMessage ?? "",
            },
            creatorMail: {
              emailSubject: res.creatorMail?.emailSubject ?? "",
              emailMessage: res.creatorMail?.emailMessage ?? "",
            },
            reSubmitMail: {
              emailSubject: res.reSubmitMail?.emailSubject ?? "",
              emailMessage: res.reSubmitMail?.emailMessage ?? "",
            },
            cancelMail: {
              emailSubject: res?.cancelMail?.emailSubject ?? "",
              emailMessage: res?.cancelMail?.emailMessage ?? "",
            },
            oneStopMail: {
              emailSubject: res?.oneStopMail?.emailSubject ?? "",
              emailMessage: res?.oneStopMail?.emailMessage ?? "",
            },
            reminder: {
              sendAutomaticRemainders:
                res?.reminder?.sendAutomaticRemainders ?? res?.reminder?.reminderEnabled ?? false,
              daysUntilFirstRemainder: res?.reminder?.daysUntilFirstRemainder ?? res?.reminder?.reminderFrequency ?? 0,
              daysBetweenRemainder: res?.reminder?.daysBetweenRemainder ?? res?.reminder?.reminderDelay ?? 0,
            },
            expiration: {
              expiryEnabled: res?.expiration?.expiryEnabled ?? true,
              expiryAfter: res?.expiration?.expiryAfter ?? 14,
              expiryWarnBefore: res?.expiration?.expiryWarnBefore ?? res?.expiration?.expiryWarn ?? 2,
            },
            reminderEnabled: res?.reminder?.sendAutomaticRemainders ?? res?.reminder?.reminderEnabled ?? false,
            reminderFrequency: res?.reminder?.daysUntilFirstRemainder ?? res?.reminder?.reminderFrequency ?? 0,
            reminderDelay: res?.reminder?.daysBetweenRemainder ?? res?.reminder?.reminderDelay ?? 0,
            expiryAfter: res?.expiration?.expiryAfter ?? 14,
            expiryWarn: res?.expiration?.expiryWarnBefore ?? res?.expiration?.expiryWarn ?? 2,
            recipientLock: false,
            messageLock: false,
            allowComments: true,
          } as unknown as FormItems;
          setInitialValue(values);
          setIsLoading(false);
        })
        .catch(() => {
          openToast({
            content: "Getting data failed",
            type: "error",
          });
          setIsLoading(false);
        });
    }
  }, []);

  const convertToBase64 = (file: any) => {
    return new Promise((resolve, reject) => {
      if (file instanceof File) {
        const reader = new FileReader();
        reader.readAsDataURL(file as Blob);
        reader.onload = () => resolve(reader.result);
        ``;
        reader.onerror = (error) => reject(error);
      } else {
        resolve({});
      }
    });
  };

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [confirmAction, setConfirmAction] = useState<any>(null);

  /* The template body PUT/POST /template take. */
  const buildPayload = (values: any) => {
    const payload: any = {
      templateId: values.templateId || id || "",
      templateName: values.templateName,
      templateDescription: values.templateDescription,
      reminder: {
        sendAutomaticRemainders: Boolean(values.reminderEnabled),
        daysUntilFirstRemainder: Number(values.reminderFrequency ?? 0),
        daysBetweenRemainder: Number(values.reminderDelay ?? 0),
      },
      expiration: {
        expiryEnabled: true,
        expiryAfter: Number(values.expiryAfter ?? 0),
        expiryWarnBefore: Number(values.expiryWarn ?? 0),
      },
      defaultMail: {
        emailSubject: values.defaultMail?.emailSubject ?? "",
        emailMessage: values.defaultMail?.emailMessage ?? "",
      },
      reviewMail: {
        emailSubject: values.reviewMail?.emailSubject ?? "",
        emailMessage: values.reviewMail?.emailMessage ?? "",
      },
      approveMail: {
        emailSubject: values.approveMail?.emailSubject ?? "",
        emailMessage: values.approveMail?.emailMessage ?? "",
      },
      completeMail: {
        emailSubject: values.completeMail?.emailSubject ?? "",
        emailMessage: values.completeMail?.emailMessage ?? "",
      },
      declineMail: {
        emailSubject: values.declineMail?.emailSubject ?? "",
        emailMessage: values.declineMail?.emailMessage ?? "",
      },
      reminderMail: {
        emailSubject: values.reminderMail?.emailSubject ?? "",
        emailMessage: values.reminderMail?.emailMessage ?? "",
      },
      copyMail: {
        emailSubject: values.copyMail?.emailSubject ?? "",
        emailMessage: values.copyMail?.emailMessage ?? "",
      },
      expiryMail: {
        emailSubject: values?.expiryMail?.emailSubject ?? "",
        emailMessage: values?.expiryMail?.emailMessage ?? "",
      },
      higherAuthorityMail: {
        emailSubject: values?.higherAuthorityMail?.emailSubject ?? "",
        emailMessage: values?.higherAuthorityMail?.emailMessage ?? "",
      },
      reSubmitMail: {
        emailSubject: values.reSubmitMail?.emailSubject ?? "",
        emailMessage: values.reSubmitMail?.emailMessage ?? "",
      },
      cancelMail: {
        emailSubject: values.cancelMail?.emailSubject ?? "",
        emailMessage: values.cancelMail?.emailMessage ?? "",
      },
      creatorMail: {
        emailSubject: values.creatorMail?.emailSubject ?? "",
        emailMessage: values.creatorMail?.emailMessage ?? "",
      },
      oneStopMail: {
        emailSubject: values.oneStopMail?.emailSubject ?? "",
        emailMessage: values.oneStopMail?.emailMessage ?? "",
      },
    };
    return payload;
  };

  const onSubmit = async (values: any) => {
    const payload = buildPayload(values);

    const formData = new FormData();
    formData.append("body", JSON.stringify(payload));
    if (id) {
      setConfirmAction(() => () => updateTemplate(id, payload));
      setIsModalVisible(true);
    } else {
      post(`${panelApiUrl}/template`, payload)
        .then((res: any) => {
          setSuccessOpen(true);
          setShowSuccess(true);
          setName(values.templateName);
          setIsSubmitLoading(false);
        })
        .catch((err) => {
          openNotification({
            title: err,
            message: "Template Creation failed",
            type: "error",
          });
          setIsSubmitLoading(false);
        });
    }
  };

  /* `quiet`: Custom Options' own Save — a toast, and the user stays on the page. */
  const updateTemplate = (id: any, payload: any, quiet = false) => {
    setIsSubmitLoading(true);
    /* The panel template service, not ePOD's `v1/spa/update-template` — a
       different service, with no record of this template. */
    put(`${panelApiUrl}/template/${id}`, payload)
      .then((res) => {
        if (res && quiet) {
          openNotification({ title: "Custom options saved", message: "", type: "success" });
        } else if (res) {
          setSuccessOpen(true);
        }
        setIsSubmitLoading(false);
      })
      .catch((err) => {
        openNotification({
          title: err,
          message: "Template Updating failed",
          type: "error",
        });
        setIsSubmitLoading(false);
      });
  };

  const handleOk = () => {
    setIsSubmitLoading(true);
    if (confirmAction) confirmAction();
    setIsModalVisible(false);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    setIsSubmitLoading(false);
  };

  const handleRemove = (file: any) => {
    if (!file.isNew) {
      setDeletedIds([...deletedIds, file.documentId]);
    }
    const index = docsList.findIndex((doc: any) => doc.documentId === file.documentId);
    const newFileList = [...docsList];
    newFileList.splice(index, 1);
    setDocsList(newFileList);
    if (file.name === selectDocument?.name || null) {
      setSelectDocument(null); // Set selectDocument to null
    } else {
      setSelectDocument(selectDocument); // Keep the selectDocument unchanged
    }
  };

  const handleFileChange = async (info: any) => {
    const { file, documentId } = info;
    try {
      const base64String: any = await convertToBase64(file);
      const documentBase64 = base64String.toString().split(",")[1];
      const newFile = {
        documentBase64: documentBase64,
        documentId: `${documentId}`,
        fileExtension: file.type?.split("/")[1],
        name: file.name.split(".")[0],
        isNew: true,
        file: file,
      };
      const totalSize = docsList?.map((item: any) => item);
      if (totalSize.length < 8) {
        setDocsList((prevList: any) => [...prevList, newFile]);
      } else {
        message.error("Adding the file exceeds the limit");
      }
    } catch (error) {
      console.error("Error converting file to base64:", error);
    }
  };

  const uploadProps: UploadProps = {
    onRemove: (file) => handleRemove(file),
    onChange: (info: any) => {
      info.documentId = newDocId;
      if (info.file.type === "application/pdf") {
        if (info.file.size <= 25 * 1024 * 1024) {
          handleFileChange(info);
        } else {
          message.error("Adding the file exceeds the 25 MB limit");
        }
      } else {
        message.error("Incorrect format, only PDF files are allowed");
      }
    },
    beforeUpload: (file) => {
      // handleFileChange(file);
      return false;
    },
  };

  const emailValidationSchema = yup.object().shape({
    emailSubject: yup.string().trim("Leading and trailing spaces are not allowed").strict(true).required("Email Subject required"),
    emailMessage: yup.string().trim("Leading and trailing spaces are not allowed").strict(true).required("Email Message required"),
  });

  const validationSchema1 = yup.object().shape({
    templateName: yup.string().trim("Leading and trailing spaces are not allowed").strict(true).required("Template Name required"),
    templateDescription: yup.string().trim("Leading and trailing spaces are not allowed").strict(true),
    reviewMail: emailValidationSchema,
    approveMail: emailValidationSchema,
    completeMail: emailValidationSchema,
    declineMail: emailValidationSchema,
    reminderMail: emailValidationSchema,
    reSubmitMail: emailValidationSchema,
    copyMail: emailValidationSchema,
    higherAuthorityMail: emailValidationSchema,
    creatorMail: emailValidationSchema,
    expiryMail: emailValidationSchema,
    // oneStopMail: emailValidationSchema, // Void Email is hidden for now
    cancelMail: emailValidationSchema,
  });
  const validationSchema2 = yup.object().shape({
    signers: yup
      .array()
      .of(
        yup.object().shape({
          name: yup
            .string()
            .trim("Name cannot include leading and trailing spaces")
            .strict(true)
            .required("Name required")
            .matches(/^[a-zA-Z\s@'/]+$/, "Invalid Name"),
          // .matches(new RegExp(/^[\w'\-,.][^0-9_!¡?÷?¿/\\+=@#$%ˆ&*(){}|~<>;:[\]]{2,}$/), "Invalid Name"),
          email: yup
            .string()
            .strict(true)
            .trim("Email address cannot include leading and trailing spaces")
            .lowercase("Email address must be a lowercase")
            .email("Email address must be a valid email")
            .required("Email address required")
            .matches(
              new RegExp(`^[a-zA-Z0-9._%+-]+@(${allowedDomainsAll.join("|")})$`),
              `Permitted Domains: ${allowedDomainsAll.join(", ")}`
            ),
        })
      )
      .test({
        test: function (signers) {
          const emailMap = new Map();
          const duplicateEmails = new Set();
          if (signers && signers.length > 0) {
            signers.forEach((signer) => {
              const { email } = signer;
              if (emailMap.has(email)) {
                duplicateEmails.add(email);
              }
              emailMap.set(email, (emailMap.get(email) || 0) + 1);
            });
          }
          if (duplicateEmails.size > 0) {
            const errors = [];
            for (let i = 0; i < (signers || []).length; i++) {
              const signer = (signers || [])[i];
              const { email } = signer;
              if (emailMap.get(email) > 1) {
                errors.push(
                  this.createError({
                    message: "Duplicate email found",
                    path: `signers[${i}].email`,
                  })
                );
              }
            }
            if (errors.length > 0) {
              throw new yup.ValidationError(errors);
            }
          }
          return true;
        },
      }),
    // .test({
    //   test: function (signers: any) {
    //     const emailMap = new Map();
    //     const duplicateEmails = new Set();
    //
    //     for (const signer of signers) {
    //       const { email } = signer;
    //
    //       if (emailMap.has(email)) {
    //         duplicateEmails.add(email);
    //       }
    //
    //       emailMap.set(email, (emailMap.get(email) || 0) + 1);
    //     }
    //
    //     if (duplicateEmails.size > 0) {
    //       const errors = [];
    //
    //       for (let i = 0; i < signers.length; i++) {
    //         const signer = signers[i];
    //         const { email } = signer;
    //
    //         if (emailMap.get(email) > 1) {
    //           errors.push(
    //             this.createError({
    //               message: "Duplicate email found",
    //               path: `signers[${i}].email`,
    //             })
    //           );
    //         }
    //       }
    //
    //       if (errors.length > 0) {
    //         throw new yup.ValidationError(errors);
    //       }
    //     }
    //
    //     return true;
    //   },
    // }),
  });
  const validationSchema = useMemo(() => {
    if (currentStep === 0) {
      return validationSchema1;
    } else {
      return validationSchema2;
    }
  }, [currentStep]);

  return (
    <>
      <Formik
        enableReinitialize={true}
        initialValues={initialValue}
        onSubmit={(values) => onSubmit(values)}
        validationSchema={validationSchema}
      >
        {(props) => {
          return (
            <>
              <CustomOptions
                setOpen={setOpen}
                open={open}
                setSave={setSave}
                /* Editing: Save in Custom Options updates the template there and then. */
                onSaved={id ? (values: any) => updateTemplate(id, buildPayload(values), true) : undefined}
                {...props}
              />{" "}
              <Form
                layout="vertical"
                onFinish={props.handleSubmit}
              >
                <Row style={{ marginTop: 16 }}>
                  <Col
                    xs={24}
                    md={currentStep === 3 || currentStep === 1 ? 24 : 16}
                  >
                    {currentStep === 0 && (
                      <>
                        <div id="step1">
                          <Row>
                            <Col span={12}>
                              <div style={{ textAlign: "left" }}>
                                <Typography.Title
                                  level={4}
                                  style={{ marginBottom: styles.whitesapce3 }}
                                >
                                  Template Details
                                </Typography.Title>
                              </div>
                            </Col>
                            <Col span={12}>
                              <div style={{ textAlign: "right" }}>
                                <Button
                                  type="default"
                                  onClick={() => setOpen(true)}
                                  icon={
                                    save ? (
                                      <Icon
                                        icon={"subway:tick"}
                                        style={{ marginRight: styles.whitespace1 }}
                                      />
                                    ) : (
                                      ""
                                    )
                                  }
                                >
                                  Custom Options
                                </Button>
                              </div>
                            </Col>
                          </Row>

                          <Row
                            align="top"
                            style={{ marginBottom: styles.whitespace3 }}
                          >
                            <Col span={24}>
                              <Form.Item required label="Template Name">
                                <Field name={`templateName`}>
                                  {({ field }: FieldProps) => (
                                    <Input
                                      {...field}
                                      status={
                                        (props.touched.templateName && props.errors.templateName && "error") || ""
                                      }
                                    />
                                  )}
                                </Field>
                                <Typography.Text type="danger">
                                  <ErrorMessage name={`templateName`} />
                                </Typography.Text>
                              </Form.Item>
                              {[
                                { key: "reviewMail", label: "For Review" },
                                { key: "copyMail", label: "For Feedback" },
                                { key: "reSubmitMail", label: "Re-Submit Email" },
                                { key: "higherAuthorityMail", label: "Document Version Email (For Reviewer & Approver)" },
                                { key: "creatorMail", label: "Document Version Email (For Creator)" },
                                { key: "approveMail", label: "Approved Email" },
                                { key: "completeMail", label: "For Completed" },
                                { key: "declineMail", label: "For Declined" },
                                { key: "reminderMail", label: "Reminder email" },
                                { key: "expiryMail", label: "Expiry Email" },
                                // { key: "oneStopMail", label: "Void Email" }, // hidden for now
                                { key: "cancelMail", label: "Cancel Email" },
                              ].map((mailConfig) => (
                                <React.Fragment key={mailConfig.key}>
                                  <Typography.Title level={5} style={{ fontWeight: "bold", marginTop: "24px" }}>
                                    {mailConfig.label}
                                  </Typography.Title>
                                  <Form.Item>
                                    <Typography.Title level={5} style={{ fontWeight: "normal" }}>
                                      Email Subject
                                    </Typography.Title>
                                    <Field name={`${mailConfig.key}.emailSubject`}>
                                      {({ field }: FieldProps) => (
                                        <Input
                                          maxLength={1000}
                                          showCount
                                          {...field}
                                          status={(props.touched[mailConfig.key as keyof typeof props.touched] as any)?.emailSubject && (props.errors[mailConfig.key as keyof typeof props.errors] as any)?.emailSubject ? "error" : ""}
                                        />
                                      )}
                                    </Field>
                                    <Typography.Text type="danger">
                                      <ErrorMessage name={`${mailConfig.key}.emailSubject`} />
                                    </Typography.Text>
                                    <div style={{ marginBottom: "16px" }} />
                                    <Typography.Title level={5} style={{ fontWeight: "normal" }}>
                                      Email Message
                                    </Typography.Title>
                                    <Field name={`${mailConfig.key}.emailMessage`}>
                                      {({ field }: FieldProps) => (
                                        <Input.TextArea
                                          rows={8}
                                          showCount
                                          maxLength={1000}
                                          style={{ height: "100px" }}
                                          {...field}
                                          status={(props.touched[mailConfig.key as keyof typeof props.touched] as any)?.emailMessage && (props.errors[mailConfig.key as keyof typeof props.errors] as any)?.emailMessage ? "error" : ""}
                                        />
                                      )}
                                    </Field>
                                    <Typography.Text type="danger">
                                      <ErrorMessage name={`${mailConfig.key}.emailMessage`} />
                                    </Typography.Text>
                                  </Form.Item>
                                </React.Fragment>
                              ))}
                            </Col>
                          </Row>
                        </div>
                      </>
                    )}
                    <Row>
                      <Col
                        xs={24}
                        md={24}
                        style={{ textAlign: "right" }}
                      >
                        <Button
                          htmlType="submit"
                          type="primary"
                          disabled={!props.isValid}
                        >
                          Submit
                        </Button>
                      </Col>
                    </Row>
                  </Col>
                </Row>
              </Form>
            </>
          );
        }}
      </Formik>
      <SuccessModal
        title={`You have successfully ${id ? "edited the template" : "created a new template"} `}
        type={"success"}
        buttonText={"Go Back"}
        redirect={`/workorder-manage-catalog?activeTab=template`}
        open={successOpen}
        setOpen={setSuccessOpen}
        message={""}
      />
      <Modal
        title="Confirm Update"
        open={isModalVisible}
        confirmLoading={isSubmitLoading}
        onOk={handleOk}
        onCancel={handleCancel}
      >
        <p>Are you sure you want to edit the template?</p>
      </Modal>
    </>
  );
};
